import ViiluDialog from 'ui/components/ViiluDialog'
import { useState, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
	TextField,
	CircularProgress,
	SelectField,
	FontIcon,
	Tab,
	Tabs,
} from 'react-md'
import { chartOfAccountsHeaders } from '../constants'
import type { UseChartOfAccountCategoryResult } from './useChartOfAccountsCategories'
import { NumericTextField } from 'ui/components/NumericTextField'
import InlineNotification from 'ui/components/InlineNotification'
import { useZodValidationResolver } from 'util/hooks/useZodValidationResolver'
import { createChartOfAccountSchema } from './validation'
import type {
	CreateTalousChartOfAccount,
	TalousChartOfAccount,
} from './validation'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { ErrorMessage } from '../../utils/components'
import { getLowestLevelCategory } from './utils'
import styled from 'styled-components'
import type { UseTalousChartOfAccountsResult } from './useTalousChartsOfAccounts'

const InlineTab = styled(Tab)`
	display: flex;
	flex-direction: row;
	gap: 4px;
	justify-content: center;
	padding: 0;
	height: 48px;

	.md-icon--tab {
		margin: 0;
	}
`

const ChartOfAccountForm = styled.form`
	padding-top: 1rem;
`
const initialFormData: CreateTalousChartOfAccount = {
	code: '',
	parent: '',
	name: '',
	vat: 0,
	groupId: 0,
	groupName: '',
}

type Props = {
	id?: string | null
	useChartOfAccounts: UseTalousChartOfAccountsResult
	useChartOfAccountCategories: UseChartOfAccountCategoryResult
	visible: boolean
	onHide: () => void
	selectedFinanceCompanyUUID: string
	onSave: (data: TalousChartOfAccount) => void
	selectedAccount?: TalousChartOfAccount | null
}

const ChartOfAccountDialog = ({
	id,
	useChartOfAccountCategories,
	visible,
	onHide,
	onSave,
	selectedAccount,
}: Props) => {
	const intl = useIntl()
	const { chartOfAccountCategories } = useChartOfAccountCategories
	const [tabIndex, setTabIndex] = useState(0)

	const resolver = useZodValidationResolver(createChartOfAccountSchema(intl))
	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
		setValue,
	} = useForm<CreateTalousChartOfAccount>({
		reValidateMode: 'onChange',
		resolver,
		defaultValues: initialFormData,
	})

	const watchedAccountNumber = useWatch({
		control,
		name: 'code',
	})

	useEffect(() => {
		if (selectedAccount) {
			setValue('code', selectedAccount.code || '')
			setValue('parent', selectedAccount.parent || '')
			setValue('name', selectedAccount.name || '')
			setValue('vat', selectedAccount.vat || 0)
			setValue('groupName', selectedAccount.groupName || '')
		} else {
			reset(initialFormData)
		}
	}, [selectedAccount, setValue, reset])

	useEffect(() => {
		if (watchedAccountNumber !== undefined) {
			const lowestCategory = getLowestLevelCategory(
				watchedAccountNumber.toString(),
				chartOfAccountCategories,
			)
			setCategory(lowestCategory.category?.name || null)
		}
	}, [watchedAccountNumber, chartOfAccountCategories])

	const [processing, setProcessing] = useState(false)
	const [category, setCategory] = useState<string | null>(null)

	const getTabs = () => {
		return [
			<InlineTab
				key={0}
				style={
					tabIndex === 0
						? {
								color: 'var(--color-primary-dark)',
								fontWeight: '700',
						  }
						: {
								color: 'var(--color-text-subtle)',
						  }
				}
				label={intl.formatMessage({
					defaultMessage: 'Tiedot',
					description: 'Title of information tab',
				})}
				icon={<FontIcon>info</FontIcon>}
				onClick={() => setTabIndex(0)}
			/>,
			<InlineTab
				key={1}
				style={
					tabIndex === 1
						? {
								color: 'var(--color-primary-dark)',
								fontWeight: '700',
						  }
						: {
								color: 'var(--color-text-subtle)',
						  }
				}
				label={intl.formatMessage({
					defaultMessage: 'Tapahtumat',
					description: 'Title of transactions tab',
				})}
				icon={<FontIcon>lists</FontIcon>}
				onClick={() => setTabIndex(1)}
			/>,
		]
	}

	const hide = () => {
		onHide()
		reset()
	}

	const onSubmit = handleSubmit(async (data) => {
		setProcessing(true)
		try {
			onSave(data as any)
			setProcessing(false)
			hide()
		} catch (error) {
			setProcessing(false)
		}
	})

	const renderFormFields = () => {
		return chartOfAccountsHeaders.map((header) => {
			const { key, title: headerTitle, type, required, menuItems } = header
			const title = typeof headerTitle === 'function' ? headerTitle(intl) : ''
			let usableMenuItems = menuItems ? menuItems(intl) : []

			if (type === 'text') {
				return (
					<>
						<Controller
							name={key as keyof CreateTalousChartOfAccount}
							control={control}
							render={({ field: { onChange, value } }) => (
								<TextField
									key={key}
									id={key}
									label={title}
									lineDirection="center"
									floating
									fullWidth
									value={String(value ?? '')}
									onChange={onChange}
									required={required}
									type="text"
									error={!!errors[key]?.message}
								/>
							)}
						/>
						{errors[key]?.message && (
							<ErrorMessage>{errors[key]?.message}</ErrorMessage>
						)}
					</>
				)
			} else if (type === 'textArea') {
				return (
					<>
						<Controller
							name={key as keyof CreateTalousChartOfAccount}
							control={control}
							render={({ field: { onChange, value } }) => (
								<TextField
									id={key}
									label={title}
									required={required}
									lineDirection="center"
									floating
									fullWidth
									rows={2}
									type="text"
									//@ts-ignore
									autoComplete="never-autocomplete"
									value={String(value || '')}
									onChange={onChange}
									error={!!errors[key]?.message}
								/>
							)}
						/>
						{errors[key]?.message && (
							<ErrorMessage>{errors[key]?.message}</ErrorMessage>
						)}
					</>
				)
			} else if (type === 'number') {
				return (
					<>
						<Controller
							name={key as keyof CreateTalousChartOfAccount}
							control={control}
							render={({ field: { onChange, value: currentValue } }) => (
								<NumericTextField
									id={key}
									label={title}
									lineDirection="center"
									floating
									fullWidth
									autoComplete="never-autocomplete"
									value={
										currentValue === null || typeof currentValue === 'boolean'
											? undefined
											: currentValue
									}
									onChange={onChange}
									required={required}
									max={9999}
									error={!!errors[key]?.message}
								/>
							)}
						/>

						{errors[key]?.message && (
							<ErrorMessage>{errors[key]?.message}</ErrorMessage>
						)}
					</>
				)
			} else if (type === 'select') {
				return (
					<>
						<Controller
							name={key as keyof CreateTalousChartOfAccount}
							control={control}
							render={({ field: { onChange, value } }) => (
								<SelectField
									id={key}
									label={title}
									required={required}
									style={{
										width: '100%',
									}}
									listStyle={{ position: 'relative' }}
									menuItems={usableMenuItems}
									value={value?.toString()}
									onChange={onChange}
									error={!!errors[key]?.message}
								/>
							)}
						/>

						{errors[key]?.message && (
							<ErrorMessage>{errors[key]?.message}</ErrorMessage>
						)}
					</>
				)
			} else if (type === 'categoryNotification') {
				return (
					<InlineNotification
						key={key}
						icon={<FontIcon>info</FontIcon>}
						textNode={
							category ? (
								<>
									<FormattedMessage
										defaultMessage="Tili kuuluu kategoriaan:"
										description="Account belongs to category"
									/>
									<strong style={{ marginLeft: '4px' }}>{category}</strong>
								</>
							) : (
								<FormattedMessage
									defaultMessage="Valitse tilille kategoria"
									description="Select a category for the account"
								/>
							)
						}
					/>
				)
			} else if (type === 'subHeader') {
				return (
					<div key={key} style={{ marginTop: '1rem' }}>
						<strong>{title}</strong>
					</div>
				)
			}
			return null
		})
	}

	const renderChildren = () => {
		return (
			<div
				className="margin-left--lg margin-right--lg"
				style={{ paddingBottom: 64, height: '100%' }}
			>
				<Tabs
					tabId="accounts-tab"
					mobile
					style={{
						flexShrink: 0,
						borderBottomStyle: 'solid',
						borderBottomColor: 'rgba(0,0,0,0.2)',
						borderBottomWidth: 1,
					}}
					alignToKeyline={false}
					defaultTabIndex={tabIndex}
					activeTabIndex={tabIndex}
					indicatorHeight={3}
				>
					{getTabs()}
				</Tabs>
				{tabIndex === 0 && (
					<ChartOfAccountForm>{renderFormFields()}</ChartOfAccountForm>
				)}
				{tabIndex === 1 && (
					<div style={{ textAlign: 'center', marginTop: '2rem' }}>
						<FormattedMessage
							defaultMessage="Tapahtumat eivät ole vielä käytössä."
							description="Transactions are not yet available."
						/>
					</div>
				)}
			</div>
		)
	}

	const actions = [
		{
			id: 'dialog-cancel',
			children: intl.formatMessage({
				defaultMessage: 'Peruuta',
				description: 'Cancel button',
			}),
			onClick: hide,
		},
		...(tabIndex === 0
			? [
					{
						id: 'dialog-ok',
						secondary: true,
						children: processing ? (
							<CircularProgress id="progress" style={{ marginTop: 0 }} />
						) : (
							intl.formatMessage({
								defaultMessage: 'Tallenna',
								description: 'Save button',
							})
						),
						onClick: () => onSubmit(),
					},
			  ]
			: []),
	]

	return (
		<ViiluDialog
			id="responsive-dialog"
			dialogStyle={{ minHeight: 'calc(100% - 16px)' }}
			footerStyle={{ marginTop: 'auto' }}
			title={
				id
					? intl.formatMessage({
							defaultMessage: 'Muokkaa tiliä',
							description: 'Title of edit bank account',
					  })
					: intl.formatMessage({
							defaultMessage: 'Uusi tili',
							description: 'Title of add bank account',
					  })
			}
			visible={visible}
			actions={actions}
			onHide={hide}
			focusOnMount={true}
			containFocus={true}
			dialogClassName="responsive-dialog"
			contentClassName="responsive-dialog-content"
			autosizeContent={false}
			modal
			portal
			paddedContent
		>
			<div style={{ height: '100%' }}>{renderChildren()}</div>
		</ViiluDialog>
	)
}

export default ChartOfAccountDialog
